import { readStorage, writeStorage } from './storage.js';
// anonId.js, anonymous-first identity for "My Kit" persistence.
//
// The server (kit.js) keys per-user gear off a client-minted UUID sent as the
// X-Anon-Id header; there is no login wall. We mint a v4 UUID once on first
// use, persist it to localStorage, and attach it to every kit request. The id
// must satisfy the server's UUID_RE (v4: version nibble 4, variant 8/9/a/b).

const KEY = 'og:anon-id';

// crypto.randomUUID is v4 and available in all secure-context browsers; the
// manual fallback covers the rare non-secure-context / old-engine case and
// produces the same v4 shape the server validates.
function mintUuid() {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch { /* fall through */ }
  // RFC 4122 v4 fallback.
  const b = new Uint8Array(16);
  (globalThis.crypto?.getRandomValues
    ? globalThis.crypto.getRandomValues(b)
    : b.forEach((_, i) => { b[i] = Math.floor(Math.random() * 256); }, b));
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // variant 10xx
  const h = [...b].map(x => x.toString(16).padStart(2, '0'));
  return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`;
}

export function getAnonId() {
  try {
    let id = readStorage(KEY);
    if (!id) { id = mintUuid(); writeStorage(KEY, id); }
    return id;
  } catch {
    // localStorage blocked (private mode etc.), mint an ephemeral id so the
    // session still works; it just won't persist across reloads.
    return mintUuid();
  }
}

const API = import.meta.env?.VITE_API_BASE || '/api';

// fetch wrapper that attaches the anon id + JSON headers. Path is relative to
// the API base (e.g. kitFetch('/kit')).
export function kitFetch(path, opts = {}) {
  const headers = { 'X-Anon-Id': getAnonId(), ...(opts.headers || {}) };
  if (opts.body != null && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  return fetch(`${API}${path}`, { ...opts, headers });
}
